// client/src/services/api.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, 
});

const ApiService = {

  startTwitterVerification: async (address) => {
    try {
      const response = await api.get(`/auth/twitter?address=${address}`);
      return response.data;
    } catch (error) {
      console.error('Error starting Twitter verification:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },


  startTwitterVerificationWithSignature: async (address, signature, message, timestamp) => {
    try {
      const response = await api.get(`/auth/twitter-with-signature`, {
        params: { 
          address, 
          signature, 
          message, 
          timestamp 
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error starting Twitter verification:', error.response?.data || error);
      throw error.response?.data?.error || error.message || 'Failed to start Twitter verification';
    }
  },


  startVerificationWithSignature: async (platform, address, signature, message, timestamp) => {
    try {
  
      const response = await api.get(`/auth/${platform}-with-signature`, {
        params: { 
          address, 
          signature, 
          message, 
          timestamp 
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error starting ${platform} verification:`, error.response?.data || error);
      throw error.response?.data?.error || error.message || `Failed to start ${platform} verification`;
    }
  },

getSupportedCategories: async () => {
  try {
    const response = await api.get('/passport/categories');
    

    if (!response.data || !response.data.categories) {
      console.warn('API returned unexpected format for categories:', response.data);
      return [];
    }
    
   
    return response.data.categories;
  } catch (error) {
    console.error('Error getting supported categories from API:', error.response?.data || error);
 
    return [];
  }},



  completeTwitterVerification: async () => {
    try {
  
      const response = await api.post('/auth/twitter/complete');
      return response.data;
    } catch (error) {
      console.error('Error completing Twitter verification:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },


  completeTwitterVerificationWithSignature: async (address, signature, message, timestamp) => {
    try {
  
      const response = await api.post('/auth/twitter/complete-with-signature', {
        address, 
        signature, 
        message, 
        timestamp
      });
      return response.data;
    } catch (error) {
      console.error('Error completing Twitter verification:', error.response?.data || error);
      throw error.response?.data?.error || error.message || 'Failed to complete Twitter verification';
    }
  },

  completeVerificationWithSignature: async (platform, address, signature, message, timestamp) => {
    try {
   
      const response = await api.post(`/auth/${platform}/complete-with-signature`, {
        address, 
        signature, 
        message, 
        timestamp
      });
      return response.data;
    } catch (error) {
      console.error(`Error completing ${platform} verification:`, error.response?.data || error);
      throw error.response?.data?.error || error.message || `Failed to complete ${platform} verification`;
    }
  },


  getTwitterVerificationStatus: async () => {
    try {
      const response = await api.get('/auth/twitter/status');
      return response.data;
    } catch (error) {
      console.error('Error checking Twitter verification status:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },


  createPassport: async (address) => {
    try {
   
      const response = await api.post('/passport/create', { address });
      return response.data;
    } catch (error) {
      console.error('Error creating passport:', error.response?.data || error);
      throw error.response?.data?.error || error.message || 'Failed to create passport';
    }
  },

 
  createPassportWithSignature: async (address, signature, message, timestamp, category, referralCode = '') => {
    try {
    
      const response = await api.post('/passport/create-with-signature', { 
        address, 
        signature, 
        message, 
        timestamp,
        category,
        referralCode 
      });
      return response.data;
    } catch (error) {
      console.error('Error creating passport:', error.response?.data || error);
      throw error.response?.data?.error || error.message || 'Failed to create passport';
    }
  },


  getPassportByAddress: async (address) => {
    try {
      const response = await api.get(`/passport/address/${address}`);
      return response.data;
    } catch (error) {
      console.error('Error getting passport by address:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },


  getPassportById: async (passportId) => {
    try {
      const response = await api.get(`/passport/${passportId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting passport by ID:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },


  prepareRevokeSocialAccount: async (passportId, platform, address) => {
    try {
      const response = await api.post('/passport/revoke', {
        passportId,
        platform,
        address,
      });
      return response.data;
    } catch (error) {
      console.error('Error preparing revocation:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },


  startGitHubVerification: async (address) => {
    try {
 
      const response = await api.get(`/auth/github?address=${address}`);
      return response.data;
    } catch (error) {
      console.error('Error starting GitHub verification:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },


  startGitHubVerificationWithSignature: async (address, signature, message, timestamp) => {
    try {
     
      const response = await api.get(`/auth/github-with-signature`, {
        params: { 
          address, 
          signature, 
          message, 
          timestamp 
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error starting GitHub verification:', error.response?.data || error);
      throw error.response?.data?.error || error.message || 'Failed to start GitHub verification';
    }
  },

  completeGitHubVerification: async () => {
    try {
      
      const response = await api.post('/auth/github/complete');
      return response.data;
    } catch (error) {
      console.error('Error completing GitHub verification:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },


  completeGitHubVerificationWithSignature: async (address, signature, message, timestamp) => {
    try {
  
      const response = await api.post('/auth/github/complete-with-signature', { 
        address, 
        signature, 
        message, 
        timestamp 
      });
      return response.data;
    } catch (error) {
      console.error('Error completing GitHub verification:', error.response?.data || error);
      throw error.response?.data?.error || error.message || 'Failed to complete GitHub verification';
    }
  },


  getGitHubVerificationStatus: async () => {
    try {
      const response = await api.get('/auth/github/status');
      return response.data;
    } catch (error) {
      console.error('Error checking GitHub verification status:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },

   /**
   * Verify Solana wallet address
   * @param {string} ethAddress - User's Ethereum address
   * @param {string} solanaPublicKey - User's Solana public key
   * @param {string} message - Signed message
   * @param {string} signature - Solana wallet signature
   * @param {number} timestamp - Timestamp when message was signed
   */
  verifySolanaAddress: async (ethAddress, solanaPublicKey, message, signature, timestamp) => {
    try {
      const response = await api.post('/auth/solana/verify', {
        ethAddress,
        solanaPublicKey,
        message,
        signature,
        timestamp
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying Solana address:', error.response?.data || error);
      throw error.response?.data?.error || error.message || 'Failed to verify Solana address';
    }
  },

  /**
   * Get Solana verification status for an Ethereum address
   * @param {string} ethAddress - User's Ethereum address
   */
  getSolanaVerificationStatus: async (ethAddress) => {
    try {
      const response = await api.get(`/auth/solana/status/${ethAddress}`);
      return response.data;
    } catch (error) {
      console.error('Error checking Solana verification status:', error.response?.data || error);
      throw error.response?.data || error;
    }
  },

    verifySolanaWithSignature: async (ethAddress, solanaPublicKey, message, signature, timestamp) => {
    try {

      const response = await api.post('/auth/solana/verify', {
        ethAddress,
        solanaPublicKey,
        message,
        signature,
        timestamp
      });
      
      return response.data;
    } catch (error) {
      console.error('Error verifying Solana address:', error.response?.data || error);
      
      // Extract error details for proper handling
      if (error.response?.data) {
        throw {
          message: error.response.data.error || 'Failed to verify Solana address',
          errorCategory: error.response.data.errorCategory || 'verification_failed',
          suggestions: error.response.data.suggestions || []
        };
      }
      
      throw {
        message: error.message || 'Failed to verify Solana address',
        errorCategory: 'network',
        suggestions: ['Check your internet connection', 'Try again']
      };
    }
  },
};



export default ApiService;